import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCountriesDataView, getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, { loadCurrentCountriesParameter } from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    countryLabel = "countryLabel",
    countryHintText = "countryHintText",
    countryIsRequired = "countryIsRequired",
    stateLabel = "stateLabel",
    stateHintText = "stateHintText",
    stateIsRequired = "stateIsRequired"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.countryLabel]: string;
        [fields.countryHintText]: string;
        [fields.countryIsRequired]: boolean;
        [fields.stateLabel]: string;
        [fields.stateHintText]: string;
        [fields.stateIsRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);

    return {
        country: state.components.contactUsForm.fieldValues["country"],
        state: state.components.contactUsForm.fieldValues["state"],
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        countries: getCurrentCountries(state)
    }
};

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentCountries
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CountryAndStateStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    stateSelect?: SelectPresentationProps;
}

export const countryAndStateStyles: CountryAndStateStyles = {
    container: {
        css: css`
            width: 100%;
            margin: 0 0 15px 0;
            padding: 0 10px;
        `
    },
    gridItem: { 
        width: 6,
        css: css`
            padding: 15px 5px;
        `
    },
};

const styles = countryAndStateStyles;

const CountryAndState: React.FC<Props> = ({ fields, country, state, setFieldValue, shouldLoadCountries, loadCurrentCountries, countries }) => {
    const { countryLabel, countryHintText, countryIsRequired,
            stateLabel, stateHintText, stateIsRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [countryErrorMessage, setCountryErrorMessage] = useState("");
    const [stateErrorMessage, setStateErrorMessage] = useState("");
    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [states, setStates] = useState<StateModel[]>([]);
    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);

    const validateCountry = () => {
        const errorMessage = countryIsRequired && !country ? (siteMessage("Field_Required", countryLabel) as string) : "";
        setCountryErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateState = () => {
        const errorMessage = stateIsRequired && !state ? (siteMessage("Field_Required", stateLabel) as string) : "";
        setStateErrorMessage(errorMessage);
        return !errorMessage;
    };

    useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, [shouldLoadCountries]);

    useEffect(() => {
        setSelectedCountry(countries?.find(c => c.id === selectedCountry?.id));
    }, [selectedCountry]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    useEffect(() => {
        contactFormContext.validators.country = validateCountry;
        if (typeof country !== "undefined") {
            validateCountry();
        }
        else{
            setSelectedCountry(undefined);
        }
    }, [country]);

    useEffect(() => {
        contactFormContext.validators.state = validateState;
        if (typeof state !== "undefined") {
            validateState();
        }
        else{
            setSelectedState(undefined);
        }
    }, [state]);

    useEffect(() => {
        contactFormContext.validators.country = validateCountry;
    }, [countryIsRequired]);

    useEffect(() => {
        contactFormContext.validators.state = validateState;
    }, [stateIsRequired]);

    const countryChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const country = countries?.find(c => c.id === event.target.value);
        setFieldValue({ key: "country", value: country?.name as string });
        setSelectedCountry(country);
    }

    const stateChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const state = selectedCountry?.states?.find(s => s.abbreviation === event.target.value);
        setFieldValue({ key: "state", value: state?.abbreviation as string });
        setSelectedState(state);
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.gridItem}>
                <Select
                    {...styles.countrySelect}
                    name="country"
                    label={countryLabel}
                    value={selectedCountry?.id || ""}
                    onChange={countryChangeHandler}
                    error={countryErrorMessage}
                    data-test-selector="country"
                    required={true}
                >
                    <option value="">{countryHintText}</option>
                    {countries?.map(co => (
                        <option key={co.id} value={co.id}>
                            {co.name}
                        </option>
                    ))}
                </Select>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <Select
                    {...styles.stateSelect}
                    label={stateLabel}
                    name="state"
                    value={selectedState?.abbreviation || ""}
                    onChange={stateChangeHandler}
                    error={stateErrorMessage}
                    data-test-selector="state"
                    disabled={!selectedCountry || (selectedCountry && selectedCountry.abbreviation.toUpperCase() !== "US" && selectedCountry.abbreviation.toUpperCase() !== "MX")}
                    required={true}
                >
                    <option value="">{stateHintText}</option>
                    {states.map(st => (
                        <option key={st.id} value={st.abbreviation}>
                            {st.abbreviation}
                        </option>
                    ))}
                </Select>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CountryAndState),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.countryLabel,
                displayName: "Country Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Country/Region",
                isRequired: true,
            },
            {
                name: fields.countryHintText,
                displayName: "Country Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select Country",
                isRequired: true,
            },
            {
                name: fields.countryIsRequired,
                displayName: "Country Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.stateLabel,
                displayName: "State Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "State",
                isRequired: true,
            },
            {
                name: fields.stateHintText,
                displayName: "State Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select State",
                isRequired: true,
            },
            {
                name: fields.stateIsRequired,
                displayName: "State Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;